/**
 * DEV-260 — widgets das etapas padrão, sobre o design system. Cada um recebe
 * o valor bruto da resposta e devolve o novo valor; quem decide visibilidade,
 * obrigatoriedade e erro é a etapa (definição + regras).
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Combobox,
  Input,
  MonthPicker,
  RadioCardGroup,
  RadioCardItem,
  TagInput,
  Textarea,
  cn,
  type ComboboxOption,
  type FormFieldControlProps,
} from '@eureca/ui';
import { PlusIcon, XIcon } from '@eureca/ui/icons';
import { FIELD_MESSAGES } from '../../shared/field-copy';
import { MAX_TEXT_LENGTH } from '../../shared/answer-validation';
import { normalizeLabel } from '../../shared/form-rules';
import { formatMoney, parseInteger, parseMoney } from '../../shared/number-input';
import { FieldShell } from './FieldShell';

export interface QuestionWidgetProps {
  name: string;
  label: string;
  required: boolean;
  hint?: string;
  placeholder?: string;
  error?: string;
  warning?: string;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur: () => void;
}

type TextKind = 'text' | 'integer' | 'money';

function displayOf(kind: TextKind, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (kind === 'money' && typeof value === 'number') return formatMoney(value);
  return String(value);
}

export function TextQuestion({ kind, ...p }: QuestionWidgetProps & { kind: TextKind }) {
  const [text, setText] = useState(() => displayOf(kind, p.value));
  // O que este campo emitiu por último: valor diferente disso veio de fora
  // (limpeza de dependente, rehidratação) e o texto é refeito.
  const lastEmitted = useRef<unknown>(p.value);
  useEffect(() => {
    if (p.value !== lastEmitted.current) {
      lastEmitted.current = p.value;
      setText(displayOf(kind, p.value));
    }
  }, [kind, p.value]);

  function emit(v: string) {
    const parsed = kind === 'integer' ? parseInteger(v) : kind === 'money' ? parseMoney(v) : v;
    lastEmitted.current = parsed;
    p.onChange(parsed);
  }

  return (
    <FieldShell {...p}>
      {(control) => (
        <div className={cn(kind === 'money' && 'relative')}>
          {kind === 'money' ? (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              R$
            </span>
          ) : null}
          <Input
            {...control}
            value={text}
            inputMode={kind === 'text' ? undefined : kind === 'integer' ? 'numeric' : 'decimal'}
            placeholder={
              kind === 'money' ? p.placeholder?.replace(/^Ex\.: R\$ /, 'Ex.: ') : p.placeholder
            }
            variant={p.error ? 'destructive' : 'default'}
            className={cn(kind === 'money' && 'pl-9')}
            onChange={(e) => {
              setText(e.target.value);
              emit(e.target.value);
            }}
            onBlur={() => {
              if (kind === 'money' && typeof p.value === 'number') setText(formatMoney(p.value));
              p.onBlur();
            }}
            data-testid={`question-${p.name}`}
          />
        </div>
      )}
    </FieldShell>
  );
}

export function TextareaQuestion(p: QuestionWidgetProps) {
  return (
    <FieldShell {...p}>
      {(control) => (
        <Textarea
          {...control}
          rows={8}
          maxLength={MAX_TEXT_LENGTH}
          className="min-h-[200px]"
          value={typeof p.value === 'string' ? p.value : ''}
          placeholder={p.placeholder}
          onChange={(e) => p.onChange(e.target.value)}
          onBlur={p.onBlur}
          data-testid={`question-${p.name}`}
        />
      )}
    </FieldShell>
  );
}

export function SelectQuestion(p: QuestionWidgetProps & { choices: string[] }) {
  const options: ComboboxOption[] = p.choices.map((c) => ({ value: c, label: c }));
  return (
    <FieldShell {...p}>
      {(control) => (
        <Combobox
          id={control.id}
          aria-describedby={control['aria-describedby']}
          aria-invalid={control['aria-invalid']}
          aria-required={control['aria-required']}
          options={options}
          value={typeof p.value === 'string' ? p.value : undefined}
          onSelect={(v) => {
            p.onChange(v);
            p.onBlur();
          }}
          placeholder={p.placeholder}
          searchPlaceholder={p.placeholder}
          error={!!p.error}
          data-testid={`question-${p.name}`}
        />
      )}
    </FieldShell>
  );
}

export function BooleanQuestion(p: QuestionWidgetProps) {
  const value = p.value === true ? 'true' : p.value === false ? 'false' : '';
  // Como todo controle do DS, os cards pintam a própria borda no erro e a mensagem
  // fica neutra (rodada 2 do QA, D27.1; mantido pelo PO na rodada 3).
  return (
    <FieldShell {...p}>
      {(control) => (
        <RadioCardGroup
          aria-describedby={control['aria-describedby']}
          aria-invalid={control['aria-invalid']}
          aria-required={control['aria-required']}
          aria-label={p.label}
          value={value}
          onValueChange={(v) => {
            p.onChange(v === 'true');
            p.onBlur();
          }}
          className="flex-nowrap"
          data-testid={`question-${p.name}`}
        >
          <RadioCardItem
            id={control.id}
            value="true"
            className="w-38"
            data-testid={`question-${p.name}-sim`}
          >
            {FIELD_MESSAGES.sim}
          </RadioCardItem>
          <RadioCardItem value="false" className="w-38" data-testid={`question-${p.name}-nao`}>
            {FIELD_MESSAGES.nao}
          </RadioCardItem>
        </RadioCardGroup>
      )}
    </FieldShell>
  );
}

export function MonthQuestion(p: QuestionWidgetProps) {
  return (
    <FieldShell {...p}>
      {(control) => (
        <MonthPicker
          {...control}
          value={typeof p.value === 'string' ? p.value : ''}
          onChange={(v) => {
            p.onChange(v);
            p.onBlur();
          }}
          placeholder={p.placeholder}
          hasError={!!p.error}
          data-testid={`question-${p.name}`}
        />
      )}
    </FieldShell>
  );
}

export function FreeTagsQuestion(
  p: QuestionWidgetProps & {
    maxItemLength: number;
    emptyMessage: string;
    tooLongMessage: string;
    validateItem?: (item: string) => string | null;
    /** Muda quando a regra do `validateItem` muda (ex.: Competências): a mensagem velha sai. */
    itemErrorResetKey?: string;
  }
) {
  const [itemError, setItemError] = useState<string | null>(null);
  useEffect(() => setItemError(null), [p.itemErrorResetKey]);
  const value = Array.isArray(p.value) ? (p.value as string[]) : [];
  return (
    <FieldShell {...p} error={itemError ?? p.error}>
      {(control) => (
        <TagInput
          {...control}
          value={value}
          onChange={(next) => {
            p.onChange(next);
            p.onBlur();
          }}
          placeholder={p.placeholder}
          maxItemLength={p.maxItemLength}
          messages={{
            empty: p.emptyMessage,
            duplicate: FIELD_MESSAGES.itemRepetido,
            tooLong: p.tooLongMessage,
          }}
          validateItem={p.validateItem ? (item) => p.validateItem?.(item) ?? null : undefined}
          onItemError={setItemError}
          addButtonLabel={FIELD_MESSAGES.adicionar}
          removeItemLabel={FIELD_MESSAGES.remover}
          data-testid={`question-${p.name}`}
        />
      )}
    </FieldShell>
  );
}

/** Item escolhido numa multi-seleção: chave estável + rótulo + valor gravado. */
export interface MultiItem {
  key: string;
  label: string;
  stored: unknown;
}

export interface MultiSelectQuestionProps extends QuestionWidgetProps {
  options: MultiItem[];
  selected: MultiItem[];
  onItemsChange: (items: MultiItem[]) => void;
  loading?: boolean;
  failed?: boolean;
  /** Rótulo da opção excludente ("Qualquer uma"). */
  exclusiveKey?: string;
  /** "Adicionar novo": item digitado, só nesta descrição (AC-16..19). */
  custom?: { maxLength: number; emptyMessage: string; tooLongMessage: string; placeholder: string };
  /** Busca no servidor (cidades do Brasil). */
  onSearchChange?: (q: string) => void;
  /** Troca o seletor (Local de trabalho usa UF + cidade). */
  renderControl?: (control: Parameters<FormFieldControlProps['children']>[0]) => ReactNode;
}

const NEW_ITEM_KEY = '__adicionar_novo__';

export function MultiSelectQuestion(p: MultiSelectQuestionProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [itemError, setItemError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);

  const exclusiveOn = !!p.exclusiveKey && p.selected.some((s) => s.key === p.exclusiveKey);
  const selectedKeys = new Set(p.selected.map((s) => s.key));
  const options: ComboboxOption[] = p.options
    .filter((o) => !selectedKeys.has(o.key))
    .map((o) => ({
      value: o.key,
      label: o.label,
      disabled: exclusiveOn && o.key !== p.exclusiveKey,
    }));
  if (p.custom) options.push({ value: NEW_ITEM_KEY, label: FIELD_MESSAGES.adicionarNovo });

  useEffect(() => {
    if (!highlight) return;
    const t = setTimeout(() => setHighlight(null), 1500);
    return () => clearTimeout(t);
  }, [highlight]);

  function commit(items: MultiItem[]) {
    p.onItemsChange(items);
    p.onBlur();
  }

  function select(key: string) {
    if (key === NEW_ITEM_KEY) {
      setAdding(true);
      return;
    }
    const item = p.options.find((o) => o.key === key);
    if (!item) return;
    if (key === p.exclusiveKey) commit([item]);
    else commit([...p.selected.filter((s) => s.key !== p.exclusiveKey), item]);
  }

  function addCustom() {
    if (!p.custom) return;
    const text = draft.trim().replace(/\s+/g, ' ');
    if (!text) return setItemError(p.custom.emptyMessage);
    if (text.length > p.custom.maxLength) return setItemError(p.custom.tooLongMessage);
    const existing = [...p.selected, ...p.options].find(
      (s) => normalizeLabel(s.label) === normalizeLabel(text)
    );
    if (existing && selectedKeys.has(existing.key)) {
      setHighlight(existing.key);
      return setItemError(FIELD_MESSAGES.itemRepetido);
    }
    commit([...p.selected, existing ?? { key: `custom:${text}`, label: text, stored: text }]);
    setDraft('');
    setItemError(null);
  }

  const error = itemError ?? p.error;
  return (
    <FieldShell
      {...p}
      error={error}
      after={
        <>
          {adding && p.custom ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex h-9 items-center gap-1 rounded-md border border-input bg-background pr-1 shadow-2xs focus-within:ring-2 focus-within:ring-primary/40">
                <input
                  autoFocus
                  value={draft}
                  placeholder={p.custom.placeholder}
                  aria-label={FIELD_MESSAGES.adicionarNovo}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setItemError(null);
                  }}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustom();
                    }
                  }}
                  className="h-full min-w-0 flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
                  data-testid={`question-${p.name}-new`}
                />
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label={FIELD_MESSAGES.adicionar}
                  onClick={addCustom}
                >
                  <PlusIcon className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label={FIELD_MESSAGES.voltar}
                  onClick={() => {
                    setAdding(false);
                    setDraft('');
                    setItemError(null);
                  }}
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{FIELD_MESSAGES.digiteEnter}</p>
            </div>
          ) : null}
          {p.selected.length > 0 ? (
            <ul
              className="flex flex-wrap gap-2"
              aria-label={p.label}
              data-testid={`question-${p.name}-list`}
            >
              {p.selected.map((item, i) => (
                <li key={item.key}>
                  <Badge
                    variant="outline"
                    className={cn('gap-1 pr-1', highlight === item.key && 'ring-2 ring-primary')}
                  >
                    {item.label}
                    <button
                      type="button"
                      aria-label={FIELD_MESSAGES.remover(item.label)}
                      className="rounded-sm p-0.5 hover:bg-muted"
                      onClick={() => commit(p.selected.filter((s) => s.key !== item.key))}
                      data-testid={`question-${p.name}-remove-${i}`}
                    >
                      <XIcon className="size-3" />
                    </button>
                  </Badge>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      }
    >
      {(control) =>
        p.renderControl ? (
          p.renderControl(control)
        ) : (
          <Combobox
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            aria-required={control['aria-required']}
            options={options}
            value={undefined}
            onSelect={select}
            placeholder={p.placeholder}
            searchPlaceholder={p.placeholder}
            loading={p.loading}
            errorMessage={p.failed ? FIELD_MESSAGES.falhaLista : undefined}
            emptyMessage={FIELD_MESSAGES.semResultado}
            onSearchChange={p.onSearchChange}
            error={!!error}
            data-testid={`question-${p.name}`}
          />
        )
      }
    </FieldShell>
  );
}
